import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../../../locales";

interface TemplateNameModalProps {
  onClose: () => void;
  onConfirm: (payload: { name: string; tags: string[] }) => void;
  isDuplicate?: boolean;
  availableTags?: string[];
}

type TagOption = {
  label: string;
  source: "definition" | "local";
};

const TemplateNameModal: React.FC<TemplateNameModalProps> = ({
  onClose,
  onConfirm,
  isDuplicate = false,
  availableTags = [],
}) => {
  const { t } = useLocale();
  const [name, setName] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [onClose]);

  const normalizeTagKey = useCallback(
    (value: string) => value.trim().toLocaleLowerCase("tr-TR"),
    []
  );

  const dedupeTags = useCallback(
    (values: string[]) =>
      Array.from(
        new Map(
          values
            .map((tag) => tag.trim())
            .filter(Boolean)
            .map((tag) => [normalizeTagKey(tag), tag])
        ).values()
      ),
    [normalizeTagKey]
  );

  const dedupeTagOptions = useCallback(
    (values: TagOption[]) => {
      const map = new Map<string, TagOption>();
      values.forEach((tag) => {
        const trimmed = tag.label.trim();
        if (!trimmed) {
          return;
        }
        const key = normalizeTagKey(trimmed);
        if (!map.has(key)) {
          map.set(key, { label: trimmed, source: tag.source });
        }
      });
      return Array.from(map.values());
    },
    [normalizeTagKey]
  );

  useEffect(() => {
    const definitionTags = dedupeTags(availableTags).map((tag) => ({
      label: tag,
      source: "definition" as const,
    }));
    setTagOptions((prev) => {
      const locals = prev.filter((tag) => tag.source === "local");
      return dedupeTagOptions([...definitionTags, ...locals]);
    });
  }, [availableTags, dedupeTagOptions, dedupeTags]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedName = name.trim();
      if (trimmedName) {
        onConfirm({ name: trimmedName, tags: dedupeTags(selectedTags) });
        onClose();
      }
    },
    [name, onConfirm, onClose, selectedTags, dedupeTags]
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const isNameValid = name.trim().length > 0;

  const tagOptionLookup = useMemo(() => {
    const map = new Map<string, TagOption>();
    tagOptions.forEach((option) => {
      map.set(normalizeTagKey(option.label), option);
    });
    return map;
  }, [normalizeTagKey, tagOptions]);

  const selectedTagItems = useMemo(
    () =>
      selectedTags.map((tag) => {
        const key = normalizeTagKey(tag);
        const option = tagOptionLookup.get(key);
        return {
          label: option?.label ?? tag,
          source: option?.source ?? "local",
        };
      }),
    [normalizeTagKey, selectedTags, tagOptionLookup]
  );

  const normalizedQuery = useMemo(
    () => normalizeTagKey(searchQuery),
    [normalizeTagKey, searchQuery]
  );
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) {
      return tagOptions;
    }
    return tagOptions.filter((option) =>
      normalizeTagKey(option.label).includes(normalizedQuery)
    );
  }, [normalizeTagKey, normalizedQuery, tagOptions]);
  const canCreateTag =
    normalizedQuery.length > 0 && !tagOptionLookup.has(normalizedQuery);

  const isTagSelected = useCallback(
    (tag: string) => {
      const key = normalizeTagKey(tag);
      return selectedTags.some(
        (selected) => normalizeTagKey(selected) === key
      );
    },
    [normalizeTagKey, selectedTags]
  );

  const handleToggleTag = useCallback((tag: string) => {
    const key = normalizeTagKey(tag);
    setSelectedTags((prev) => {
      const hasTag = prev.some(
        (selected) => normalizeTagKey(selected) === key
      );
      if (hasTag) {
        return prev.filter(
          (selected) => normalizeTagKey(selected) !== key
        );
      }
      return [...prev, tag.trim()];
    });
  }, [normalizeTagKey]);

  const handleRemoveTag = useCallback(
    (tag: string) => {
      const key = normalizeTagKey(tag);
      setTagOptions((prev) =>
        prev.filter(
          (option) =>
            !(
              option.source === "local" &&
              normalizeTagKey(option.label) === key
            )
        )
      );
      setSelectedTags((prev) =>
        prev.filter((selected) => normalizeTagKey(selected) !== key)
      );
    },
    [normalizeTagKey]
  );

  const handleCreateTag = useCallback(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      return;
    }
    const key = normalizeTagKey(trimmed);
    const existingOption = tagOptionLookup.get(key);
    if (existingOption) {
      setSelectedTags((prev) => {
        const hasTag = prev.some(
          (selected) => normalizeTagKey(selected) === key
        );
        if (hasTag) {
          return prev;
        }
        return [...prev, existingOption.label];
      });
      setSearchQuery("");
      return;
    }
    setTagOptions((prev) =>
      dedupeTagOptions([...prev, { label: trimmed, source: "local" }])
    );
    setSelectedTags((prev) => dedupeTags([...prev, trimmed]));
    setSearchQuery("");
  }, [
    dedupeTagOptions,
    dedupeTags,
    normalizeTagKey,
    searchQuery,
    tagOptionLookup,
  ]);

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div
        className="qe-modal__content"
        ref={contentRef}
        tabIndex={-1}
        style={{ width: "500px", maxWidth: "calc(100vw - 32px)" }}
      >
        <header className="qe-modal__header">
          <div>
            <h2 className="qe-modal__title">
              {isDuplicate
                ? t("templateName.titleNew")
                : t("templateName.titleEdit")}
            </h2>
            <p className="qe-modal__subtitle">{t("templateName.subtitle")}</p>
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t("templateName.close")}
          >
            ✕
          </button>
        </header>

        <form className="qe-modal__body" onSubmit={handleSubmit}>
          <div className="qe-form__field">
            <label htmlFor="template-name-input" className="qe-form__label">
              {t("templateName.nameLabel")}
            </label>
            <input
              id="template-name-input"
              ref={inputRef}
              type="text"
              className="qe-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("templateName.namePlaceholder")}
              autoComplete="off"
              style={{ padding: "12px 14px", fontSize: "14px" }}
            />
          </div>
          <div className="qe-form__field">
            <label className="qe-form__label">{t("templateName.tagsLabel")}</label>
            {selectedTagItems.length > 0 ? (
              <div className="qe-chip-row">
                {selectedTagItems.map((tag) => {
                  const isActive = true;
                  return (
                    <div
                      key={normalizeTagKey(tag.label)}
                      className={`qe-chip${isActive ? " qe-chip--active" : ""}`}
                    >
                      <button
                        type="button"
                        className="qe-chip__label"
                        onClick={() => handleToggleTag(tag.label)}
                        aria-pressed={isActive}
                      >
                        {tag.label}
                      </button>
                      {tag.source === "local" ? (
                        <button
                          type="button"
                          className="qe-chip__remove"
                          onClick={() => handleRemoveTag(tag.label)}
                          aria-label={`${tag.label}`}
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="qe-form__help">{t("templateName.noTagsSelected")}</p>
            )}
            <div className="qe-multi-select" style={{ marginTop: "8px" }}>
              <div className="qe-multi-select__popover">
                <div className="qe-multi-select__input">
                  <input
                    type="text"
                    className="qe-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleCreateTag();
                      }
                    }}
                    placeholder={t("templateName.searchPlaceholder")}
                    autoComplete="off"
                  />
                </div>
                {canCreateTag ? (
                  <button
                    type="button"
                    className="qe-multi-select__create"
                    onClick={handleCreateTag}
                  >
                    + "{searchQuery.trim()}" etiketini ekle
                  </button>
                ) : null}
                {filteredOptions.length > 0 ? (
                  <div className="qe-multi-select__list" role="listbox">
                    {filteredOptions.map((option) => {
                      const key = normalizeTagKey(option.label);
                      const isActive = isTagSelected(option.label);
                      return (
                        <div
                          key={key}
                          className={`qe-multi-select__row${
                            isActive ? " qe-multi-select__row--active" : ""
                          }`}
                        >
                          <label className="qe-multi-select__label">
                            <input
                              type="checkbox"
                              className="qe-multi-select__checkbox"
                              checked={isActive}
                              onChange={() => handleToggleTag(option.label)}
                            />
                            <span>{option.label}</span>
                          </label>
                          {option.source === "local" ? (
                            <button
                              type="button"
                              className="qe-multi-select__remove"
                              onClick={() => handleRemoveTag(option.label)}
                              aria-label={`${option.label}`}
                            >
                              ✕
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {filteredOptions.length === 0 && !canCreateTag ? (
                  <div className="qe-multi-select__empty">
                    {t("templateName.noMatchingTags")}
                  </div>
                ) : null}
              </div>
            </div>
            <p className="qe-form__help">
              {t("templateName.tagHelp")}
            </p>
          </div>

          <div className="qe-modal__footer">
            <button
              type="button"
              className="qe-button qe-button--secondary"
              onClick={handleCancel}
            >
              {t("templateName.cancel")}
            </button>
            <button
              type="submit"
              className="qe-button qe-button--primary"
              disabled={!isNameValid}
            >
              {t("templateName.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateNameModal;
